import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Popconfirm, Switch, Tooltip } from 'antd'
import clsx from 'clsx'
import { type FC, useEffect, useRef, useState } from 'react'
import { FaGripVertical } from 'react-icons/fa'

import { Button, Modal, ModalBody, ModalFooter } from '@/components/elements'
import {
  ACTIONS,
  CONFIRMATION_MESSAGES,
  DESCRIPTIONS,
  LABELS
} from '@/constants/common/message.constants'

import {
  type ColumnLayoutDefinition,
  type ResolvedColumn,
  canToggleColumnVisibility,
  moveColumnWithinGroup,
  setColumnHidden,
  syncColumnsDraft
} from '../../-models/column-layout'

type Props = {
  open: boolean
  onClose: () => void
  columns: ResolvedColumn[]
  definitions: ColumnLayoutDefinition[]
  labelOf: (key: string) => string
  /** 「保存して反映」で確定した列の状態 */
  onSave: (next: ResolvedColumn[]) => void
  /** 「初期状態に戻す」の確認で了承されたとき */
  onReset: () => void
}

type ItemProps = {
  column: ResolvedColumn
  label: string
  /** 並べ替えのつまみを出すか（固定する列は出さない） */
  isSortable: boolean
  canToggle: boolean
  disabledReason?: string
  onToggle: (isVisible: boolean) => void
}

const SortableColumnItem: FC<ItemProps> = ({
  column,
  label,
  isSortable,
  canToggle,
  disabledReason,
  onToggle
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: column.key, disabled: !isSortable })

  const visibilitySwitch = (
    <Switch
      size="small"
      checked={!column.isHidden}
      disabled={!canToggle}
      onChange={onToggle}
      aria-label={label}
    />
  )

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={clsx(
        'flex items-center gap-2 rounded border border-solid border-gray-200 bg-white px-2 py-1',
        isDragging && 'z-10 shadow-md'
      )}
      data-testid={`column-setting-${column.key}`}
    >
      {isSortable && (
        <button
          ref={setActivatorNodeRef}
          type="button"
          aria-label={`${label}: ${LABELS.REORDER_COLUMN}`}
          // 掴めることが分かるよう、つまみに乗ったら掴む形のカーソルにする（9/30 の FB-4）
          className={clsx(
            'flex border-0 bg-transparent p-1 text-gray-400',
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          )}
          {...attributes}
          {...listeners}
        >
          <FaGripVertical />
        </button>
      )}
      <span className="flex-1">{label}</span>
      {disabledReason ? (
        <Tooltip title={disabledReason}>{visibilitySwitch}</Tooltip>
      ) : (
        visibilitySwitch
      )}
    </li>
  )
}

/**
 * 配送依頼一覧の「表示項目設定」モーダル（#3690 VOC_PJ-577、確定版 §2.2）。
 *
 * - 項目を縦にすべて並べ、右側の ON／OFF スイッチで表示・非表示を切り替える
 * - 変更は「保存して反映」で確定し、「キャンセル」なら変更前のまま（モーダル内は下書きで持つ）
 * - 固定しない列は、つまみのドラッグで並べ替える（9/30 の FB-5。全項目が縦に並ぶので右端の列も一気に先頭側へ移せる）
 * - 固定する列にはつまみを出さない。固定する列の並び替えと固定の出し入れはレイアウト編集モードで行い、
 *   区画の見出しの下でそう案内する（固定の区画の中だけ動かせると、区画をまたいで固定を外せると誤解される）
 * - 「初期状態に戻す」は確認のうえで保存値を消す
 */
export const ShipmentOrdersColumnSettingsModal: FC<Props> = ({
  open,
  onClose,
  columns,
  definitions,
  labelOf,
  onSave,
  onReset
}) => {
  const [draft, setDraft] = useState(columns)
  const wasOpenRef = useRef(false)

  // 開いたときだけ、いま一覧に当たっている状態から下書きを作り直す。開いている間に列が変わったら
  // （拠点のカスタムキー設定の取得完了など）、下書きを作り直さずに増減だけ合わせる（未保存の変更を消さない）
  useEffect(() => {
    if (open && !wasOpenRef.current) setDraft(columns)
    else if (open) setDraft((current) => syncColumnsDraft(current, columns))
    wasOpenRef.current = open
  }, [open, columns])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    setDraft((current) =>
      moveColumnWithinGroup(current, String(active.id), String(over.id))
    )
  }

  const disabledReasonOf = (column: ResolvedColumn): string | undefined => {
    if (canToggleColumnVisibility(draft, definitions, column.key))
      return undefined
    const definition = definitions.find(({ key }) => key === column.key)
    return definition?.canHide
      ? DESCRIPTIONS.LAST_FIXED_COLUMN
      : DESCRIPTIONS.UNHIDEABLE_COLUMN
  }

  const renderGroup = (
    title: string,
    group: ResolvedColumn[],
    options: { isSortable: boolean; help?: string }
  ) => (
    <section className="flex flex-col gap-1">
      <h3 className="m-0 text-xs font-bold text-secondary">{title}</h3>
      {options.help && (
        <p className="m-0 text-xs text-secondary">{options.help}</p>
      )}
      <SortableContext
        items={group.map(({ key }) => key)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {group.map((column) => {
            const disabledReason = disabledReasonOf(column)
            return (
              <SortableColumnItem
                key={column.key}
                column={column}
                label={labelOf(column.key)}
                isSortable={options.isSortable}
                canToggle={disabledReason === undefined}
                disabledReason={disabledReason}
                onToggle={(isVisible) =>
                  setDraft((current) =>
                    setColumnHidden(
                      current,
                      definitions,
                      column.key,
                      !isVisible
                    )
                  )
                }
              />
            )
          })}
        </ul>
      </SortableContext>
    </section>
  )

  return (
    <Modal
      title={ACTIONS.COLUMN_SETTINGS}
      open={open}
      onCancel={onClose}
      width={400}
    >
      <ModalBody>
        <p className="mt-0 text-xs text-secondary">
          {DESCRIPTIONS.COLUMN_SETTINGS_HELP}
        </p>
        {/* macOS のオーバーレイ型スクロールバーは幅を取らずに中身の上に出るため、
            右に余白を取ってスイッチと重ならないようにする */}
        <div className="max-h-[60vh] overflow-y-auto pr-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <div className="flex flex-col gap-4">
              {renderGroup(
                LABELS.FIXED_COLUMNS,
                draft.filter(({ isFixed }) => isFixed),
                { isSortable: false, help: DESCRIPTIONS.FIXED_COLUMNS_HELP }
              )}
              {renderGroup(
                LABELS.SCROLLABLE_COLUMNS,
                draft.filter(({ isFixed }) => !isFixed),
                { isSortable: true }
              )}
            </div>
          </DndContext>
        </div>
      </ModalBody>
      <ModalFooter className="!justify-between">
        <Popconfirm
          title={CONFIRMATION_MESSAGES.RESET_COLUMN_LAYOUT}
          okText={ACTIONS.RESET_COLUMN_LAYOUT}
          cancelText={ACTIONS.CANCEL}
          onConfirm={() => {
            onReset()
            onClose()
          }}
        >
          <Button type="default">{ACTIONS.RESET_COLUMN_LAYOUT}</Button>
        </Popconfirm>
        <div className="flex gap-2">
          <Button type="default" onClick={onClose}>
            {ACTIONS.CANCEL}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              onSave(draft)
              onClose()
            }}
          >
            {ACTIONS.SAVE_AND_APPLY}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}
